import React, { useState } from "react";
import { IconButton, Menu, MenuItem } from "@mui/material";
import { json, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import ConfirmDeleteDialog from "../../shared/ConfirmDeleteDialog";

/**
 * 担当者カード右上のメニュー（編集／削除）
 */
const CompanyUserMenu = ({
  companyUserId,
  user,
  currentCompanyUser,
  allUsers,
  setSelectedUser,
  setOpen,
  setMode,
  setUsers,
}) => {
  const navigate = useNavigate();

  // メニューボタン（縦三点）のアンカー要素
  const [anchorEl, setAnchorEl] = useState(null);

  // 削除確認ダイアログの表示状態
  const [showDialog, setShowDialog] = useState(false);

  // 編集モーダルの表示状態
  const [showEditModal, setShowEditModal] = useState(false);

  // メニューが開いているかどうか
  const open = Boolean(anchorEl);

  // メニューを開く（クリックした要素を基準に）
  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setShowDialog(false);
  };

  // メニューを閉じる
  const handleCloseMenu = () => setAnchorEl(null);

  // 「削除する」クリック時：ダイアログを開き、メニューは閉じる
  const handleOpenDialog = () => {
    setShowDialog(true);
    handleCloseMenu();
  };

  const [isDeleting, setIsDeleting] = useState(false);

  // ダイアログのキャンセルまたは外側クリックで閉じる
  const handleCloseDialog = () => {
    if (isDeleting) return; // 削除中は閉じさせない
    setShowDialog(false);
  };

  /**
   * 「削除」を確定したときの処理
   * DELETE リクエストを送り、完了後にページをリロード
   */
  const handleConfirmDelete = () => {
    if (isDeleting) return; // 2重発火防止

    if (user.role === "admin") {
      toast.error("この担当者は削除できません");
      return;
    }
    setIsDeleting(true);
    const csrfToken = document.querySelector(
      'meta[name="csrf-token"]'
    )?.content;

    fetch(`/company/users/${companyUserId}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
      },
    })
      .then((res) => {
        if (res.ok) {
          setIsDeleting(false);
          setShowDialog(false);
          setUsers((prev) => prev.filter((u) => u.id !== companyUserId));
          toast.success("担当者を削除しました。");
        } else {
          toast.error("削除に失敗しました");
          setIsDeleting(false);
        }
      })
      .catch((err) => {
        console.error("削除エラー:", err);
        toast.error("削除に失敗しました");
        setIsDeleting(false);
      });
  };

  return (
    <>
      {/* 縦三点ボタン */}
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          handleOpenMenu(e);

          const sortedGenerals = [...allUsers]
            .filter((u) => u.role === "general")
            .sort((a, b) => a.id - b.id);
          const index = sortedGenerals.findIndex((u) => u.id === user.id);

          setSelectedUser({
            id: user.id,
            number: user.number,
            last_name: user.last_name,
            first_name: user.first_name,
            last_name_kana: user.last_name_kana,
            first_name_kana: user.first_name_kana,
            email: user.email,
            prText: user.pr_text,
            role: user.role,
            generalIndex: index >= 0 ? index + 1 : undefined,
          });
          setMode("edit");
        }}
        size="small"
      >
        <MoreHorizIcon />
      </IconButton>

      {/* メニュー（編集・削除） */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={(event, reason) => {
          // メニューが閉じられる際に、Cardへの伝播を止める
          if (event?.stopPropagation) event.stopPropagation();
          handleCloseMenu();
        }}
      >
        {/* 編集モーダル開く */}
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            handleCloseMenu();

            const sortedGenerals = [...allUsers]
              .filter((u) => u.role === "general")
              .sort((a, b) => a.id - b.id);
            const index = sortedGenerals.findIndex((u) => u.id === user.id);

            setSelectedUser({
              id: user.id,
              number: user.number,
              last_name: user.last_name,
              first_name: user.first_name,
              last_name_kana: user.last_name_kana,
              first_name_kana: user.first_name_kana,
              email: user.email,
              prText: user.pr_text,
              role: user.role,
              generalIndex: index >= 0 ? index + 1 : undefined,
              department_id: user.department_id ?? user.departmentId ?? null,
              department_name:
                user.department_name ?? user.departmentName ?? null,
            });

            setShowEditModal(true);
            setOpen(true);
          }}
        >
          編集する
        </MenuItem>

        {/* 削除ボタン：確認ダイアログを開く （adminのみ表示）*/}
        {currentCompanyUser?.role === "admin" && (
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();

              if (user.role === "admin") {
                toast.error("この担当者は削除できません");
                handleCloseMenu();
                return;
              }

              setSelectedUser({
                id: user.id,
                number: user.number,
                last_name: user.last_name,
                first_name: user.first_name,
                last_name_kana: user.last_name_kana,
                first_name_kana: user.first_name_kana,
                email: user.email,
                prText: user.pr_text,
              });

              setMode("edit");
              handleOpenDialog();
            }}
            sx={{ color: "red" }}
          >
            削除する
          </MenuItem>
        )}
      </Menu>

      {/* 削除確認ダイアログ */}
      <ConfirmDeleteDialog
        open={showDialog}
        onClose={handleCloseDialog}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};

export default CompanyUserMenu;
